import type { AuthStateUpdate, SetGlobalDataCache } from 'App/AppContextProvider';
import { getIdentityProviderErrorCode } from 'components/Notification/ErrorMessageHelper';
import { setGlobalOrgUnitId } from 'globalState';
import CurrentUser from 'models/currentuser';
import { IdentityProviderType, OidcIssuerInfo } from 'models/identityProvider';
import Tenant from 'models/tenant';
import User from 'models/user';
import { ErrorResponse, OidcClient, User as OidcUser } from 'oidc-client-ts';
import http from 'services/Api/httpService';
import { apiGetOidcLoginInfo } from 'services/Api/identityService';
import { apiLoginUser } from 'services/Api/userService';
import { apiClientId } from 'services/Auth/authConfig';
import { AuthErrorCodes } from 'services/Auth/authErrors';
import { IAuthProvider } from 'services/Auth/authProvider';
import { completeLoginProfile } from 'services/Auth/loginProfile';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import {
  getLocalStorageData,
  LocalStorageKeys,
  removeLocalStorageData,
  setLocalStorageData,
} from 'utils/localstorage';
import {
  getSessionStorageData,
  removeSessionStorageData,
  SessionStorageKeys,
  setSessionStorageData,
} from 'utils/sessionStorage';
import { getGoogleClientSettings, getOidcClientSettings, getOidcRedirectUri } from './oidcConfig';

//
// Sign-in through OpenID Connect, for two providers that share everything but the issuer: Google Workspace
// (one fixed issuer, Google's) and the organization sign-in (any allow-listed issuer, looked up at the API
// by e-mail domain or issuer URL). Redirect flow only (no popup): the browser goes to the issuer and lands
// back on the app root with a code, which the API's token proxy exchanges. The id_token is the bearer token
// the API accepts; it is renewed here through the API on proof of the previous one, never through a refresh
// token in the browser. Every Microsoft Graph or Power BI token request is refused with a typed error instead
// of opening a Microsoft sign-in.
//
// A sign-in remembers where it started (SessionStorageKeys.OidcReturnUrl) and lands back there, whatever
// round trips it took on the way (the consent retry, a silent attempt that found no session). A landing is
// recognized by the root path, the marker this provider set before the redirect (OidcPendingProvider) and the
// code (or error) in the query; a code without the marker is somebody else's (an Entra admin-consent decline
// carries state and error too) and is left alone.
//
// The errors this provider raises carry the codes of AuthErrorCodes (services/Auth/authErrors.ts), which
// ErrorMessageService maps to their text; the codes live there so that the mirrored folders never import
// this one.
//
//renew the id_token when it has less than this left (or less than half its lifetime, for shorter tokens)
const renewThresholdSeconds = 300;

//what the API's token proxy adds when Google handed out no refresh token and none is stored
type TokenResponseExtras = { needs_consent?: boolean };

//the profile claims this provider reads from an id_token
type OidcProfile = {
  sub?: string;
  iss?: string;
  hd?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  picture?: string;
  iat?: number;
  exp?: number;
};

type RenewResponse = { id_token: string; access_token?: string; token_type?: string; expires_in?: number; scope?: string };

type SignInRedirectOptions = {
  //where to land after the round trip; by default where the first redirect of this sign-in started
  returnUrl?: string;
  //false when a person asks for another account: the issuer's form must not be prefilled with the one being left
  withLoginHint?: boolean;
};

export class OidcAuthProvider implements IAuthProvider {
  readonly providerType: IdentityProviderType;

  //Google: fixed for the life of the provider; organization sign-in: built for the issuer in play
  private client: OidcClient | undefined;

  //the signed-in user, kept in memory beside the session store so the synchronous readers can answer
  private currentUser: OidcUser | undefined;

  //one renewal at a time: concurrent token requests share it
  private renewInFlight: Promise<OidcUser> | undefined;

  constructor(providerType: IdentityProviderType = IdentityProviderType.Google) {
    this.providerType = providerType;
    if (this.isGoogle) {
      this.client = new OidcClient(getGoogleClientSettings());
    }
  }

  private get isGoogle(): boolean {
    return this.providerType === IdentityProviderType.Google;
  }

  //the API's endpoints of this provider: identity/google/* or identity/oidc/*
  private get apiPath(): string {
    return this.isGoogle ? 'google' : 'oidc';
  }

  initialize = async (): Promise<void> => {
    this.currentUser = this.loadUser();
  };

  //the app root with a code (or an error) and the state of a sign-in THIS provider started
  isRedirectCallback = (): boolean => {
    return OidcAuthProvider.isCallbackLanding() && OidcAuthProvider.pendingProviderType() === this.providerType;
  };

  //the shape of a callback query: the state, and a code or an error
  static isCallbackQuery(search: string): boolean {
    const params = new URLSearchParams(search);

    return params.has('state') && (params.has('code') || params.has('error'));
  }

  //a landing of ours: the app root is the only redirect uri, and only a sign-in this tab started has a pending
  //provider. An Entra admin-consent decline (?error=access_denied&state= on the integration page) is neither
  static isCallbackLanding(): boolean {
    return (
      window.location.pathname === '/' &&
      OidcAuthProvider.pendingProviderType() !== undefined &&
      OidcAuthProvider.isCallbackQuery(window.location.search)
    );
  }

  //which of the two providers started the sign-in that is landing
  static pendingProviderType(): IdentityProviderType | undefined {
    const stored = getSessionStorageData(SessionStorageKeys.OidcPendingProvider);
    const parsed = stored !== undefined ? Number.parseInt(stored) : Number.NaN;

    return Number.isNaN(parsed) ? undefined : (parsed as IdentityProviderType);
  }

  //where the sign-in in flight started from, when one is in flight
  static pendingReturnUrl(): string | undefined {
    return getSessionStorageData(SessionStorageKeys.OidcReturnUrl);
  }

  //the query part of the return url (the deep link's ouid, tid, idp), for the org unit to be set before the login call
  static pendingReturnQuery(): string | undefined {
    const url = OidcAuthProvider.pendingReturnUrl();
    const queryStart = url?.indexOf('?') ?? -1;

    return url && queryStart >= 0 ? url.slice(queryStart) : undefined;
  }

  //a load that is not a landing ends whatever sign-in was in flight: no stale marker or return url
  static clearPendingSignIn(): void {
    removeSessionStorageData(SessionStorageKeys.OidcPendingProvider);
    removeSessionStorageData(SessionStorageKeys.OidcReturnUrl);
  }

  handleRedirectResponse = async (
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<boolean> => {
    if (!this.isRedirectCallback()) {
      return false;
    }

    try {
      authStateUpdate(undefined, undefined, true, undefined);
      logger.debug(`Method: OIDC handleRedirectResponse (${this.apiPath})`);

      const response = await this.getClient().processSigninResponse(window.location.href);
      const user = new OidcUser(response);
      this.storeUser(user);
      removeSessionStorageData(SessionStorageKeys.OidcPendingProvider);

      //Google hands the refresh token out on the first consent only. When the proxy found none, one more
      //round trip with prompt=consent, once, so that a stale grant cannot loop the browser. The return url
      //stays: the retry lands back on it
      if (this.isGoogle && (response as unknown as TokenResponseExtras).needs_consent === true) {
        if (!getSessionStorageData(SessionStorageKeys.OidcConsentRetried)) {
          setSessionStorageData(SessionStorageKeys.OidcConsentRetried, 'true');
          logger.debug('No refresh token for this identity yet: asking for consent once');
          await this.signinRedirect('select_account consent', this.directoryOf(user));

          return true;
        }

        logger.debug('Consent was already asked for in this session; continuing without a refresh token');
      }

      //a landing whose token has time left lifts the silent-attempt guard; one that is stale on arrival (clock
      //skew, a very short lifetime) does not, so a session that cannot be renewed never loops the browser
      if (OidcAuthProvider.secondsLeft(user) > OidcAuthProvider.renewThreshold(user)) {
        removeSessionStorageData(this.silentAttemptedKey);
      }

      removeSessionStorageData(SessionStorageKeys.OidcConsentRetried);

      //an organization switch that needed a new sign-in carries its target org unit across the redirect
      const pendingOrgUnit = getSessionStorageData(this.pendingOrgUnitKey);
      if (pendingOrgUnit) {
        removeSessionStorageData(this.pendingOrgUnitKey);
        setGlobalOrgUnitId(pendingOrgUnit);
      }

      //the callback query is spent: back to where the sign-in started, so that route renders once the profile is in
      const returnUrl = OidcAuthProvider.pendingReturnUrl();
      removeSessionStorageData(SessionStorageKeys.OidcReturnUrl);
      OidcAuthProvider.restoreLocation(returnUrl ?? window.location.pathname);

      await this.setUserProfile(authStateUpdate, setGlobalDataCache);

      return true;
    } catch (err) {
      window.history.replaceState({}, document.title, window.location.pathname);
      logger.debug('OIDC redirect response error', err);

      const error = this.mapRedirectError(err);
      if (error === undefined) {
        //no sign-in happened (prompt=none found no session, the chooser was closed): the marker goes, the
        //return url stays so that the sign-in button on the welcome screen still lands on the deep link
        removeSessionStorageData(SessionStorageKeys.OidcPendingProvider);
      } else {
        OidcAuthProvider.clearPendingSignIn();
      }

      authStateUpdate(false, CurrentUser.getEmptyUser(), false, error);

      return true;
    }
  };

  //
  // Silent sign-in: a stored session that still works, a renewal, or one silent round trip through the
  // issuer's session (prompt=none answers without any UI when there is none), then the welcome screen.
  //
  ssoSilent = async (
    directoryId: string | undefined,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<void> => {
    try {
      logger.debug(`Method: OIDC ssoSilent (${this.apiPath})`);
      authStateUpdate(undefined, undefined, true, undefined);

      const user = this.loadUser();

      if (user) {
        try {
          await this.getUsableUser(user, false);
          await this.setUserProfile(authStateUpdate, setGlobalDataCache);

          return;
        } catch (err) {
          if (!OidcAuthProvider.isSignInAgainError(err)) {
            //a network error, a 429, a 5xx: the session is fine, the renewal is not. The welcome screen says so
            //and a reload tries again with the session still in place
            logger.debug('Renewal of the stored session failed', err);
            authStateUpdate(false, CurrentUser.getEmptyUser(), false, err);

            return;
          }

          logger.debug('Stored session cannot be renewed');
          this.removeUser();
        }
      }

      //the organization sign-in can only be silent for an issuer it knows: the one asked for, or the last one
      if (!getSessionStorageData(this.silentAttemptedKey) && (this.isGoogle || directoryId || this.loadIssuerInfo())) {
        setSessionStorageData(this.silentAttemptedKey, 'true');
        await this.signinRedirect('none', directoryId);

        return;
      }

      //no session at the issuer either: the welcome screen with the sign-in button
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
    } catch (err) {
      logger.debug('Error', err);
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, err);
    }
  };

  //directoryId: the Workspace domain for Google, the issuer URL for the organization sign-in
  login = async (
    directoryId: string | undefined,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
    switchAccount: boolean = false,
  ): Promise<void> => {
    try {
      authStateUpdate(undefined, undefined, true, undefined);
      logger.debug(`Method: OIDC login (${this.apiPath})`);

      //Google shows its account chooser; an organization's issuer shows a fresh sign-in form (prompt=login,
      //OpenID Connect Core 3.1.2.1) when the person asks for another account, without the hint of the one left
      const prompt = this.isGoogle ? 'select_account' : switchAccount ? 'login' : undefined;
      await this.signinRedirect(prompt, directoryId, { withLoginHint: !(switchAccount && !this.isGoogle) });
    } catch (err) {
      logger.debug('Error', err);
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, err);
    }
  };

  logout = async (authStateUpdate: AuthStateUpdate): Promise<void> => {
    const user = this.loadUser();

    try {
      //the server side of the session: the stored refresh token is revoked and removed. Best effort: the
      //browser session ends either way
      if (user?.id_token) {
        await http.post(`/identity/${this.apiPath}/logout`, { id_token: user.id_token });
      }
    } catch (err) {
      logger.debug('Logout at the API failed', err);
    } finally {
      this.removeUser();
      removeSessionStorageData(this.silentAttemptedKey);
      removeSessionStorageData(this.pendingOrgUnitKey);
      removeSessionStorageData(SessionStorageKeys.OidcConsentRetried);
      OidcAuthProvider.clearPendingSignIn();
      //the organization sign-in forgets its issuer; a Google sign-out leaves the other provider's alone
      if (!this.isGoogle) {
        removeSessionStorageData(SessionStorageKeys.OidcIssuerInfo);
        removeLocalStorageData(undefined, LocalStorageKeys.LastOidcIssuer);
      }
      //the remembered name feeds every provider's login hint, Microsoft's included: it goes, as it does on a
      //Microsoft sign-out
      removeLocalStorageData(undefined, LocalStorageKeys.UserName);
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
      //Google has no end-session endpoint for a relying party, and an organization's issuer keeps its own
      //session; the app's own logout page closes ours
      window.location.assign(getOidcRedirectUri() + 'logout');
    }
  };

  //an organization on another directory: the stored session serves it when it is that directory's, otherwise
  //the issuer is asked for that directory, and the org unit to land in travels across the redirect
  switchTenant = async (
    tenant: Tenant,
    authStateUpdate: AuthStateUpdate,
    setGlobalDataCache: SetGlobalDataCache,
  ): Promise<void> => {
    try {
      authStateUpdate(undefined, undefined, true, undefined);
      logger.debug(`Method: OIDC switchTenant: ${tenant.name}`);

      const user = this.loadUser();
      const domain = tenant.providerTenantId?.toLowerCase();

      if (user && domain && this.directoryOf(user)?.toLowerCase() === domain) {
        await this.setUserProfile(authStateUpdate, setGlobalDataCache);

        return;
      }

      const orgUnitId = tenant.parentId ? tenant.id : undefined;
      if (orgUnitId) {
        setSessionStorageData(this.pendingOrgUnitKey, orgUnitId);
      }

      //an organization switch lands on the dashboard: a deep entity route of the organization left would not exist
      await this.signinRedirect(this.isGoogle ? 'select_account' : undefined, domain, { returnUrl: '/' });
    } catch (err) {
      logger.debug('Error', err);
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, err);
    }
  };

  //
  // The profile: the back-end login answers who this is; before a tenant exists (a trial start) the
  // id_token claims stand in for what Graph provides an Entra user
  //
  setUserProfile = async (authStateUpdate: AuthStateUpdate, setGlobalDataCache: SetGlobalDataCache): Promise<void> => {
    try {
      logger.debug(`Method: OIDC setUserProfile (${this.apiPath})`);

      const user = this.loadUser();
      if (!user) {
        authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);

        return;
      }

      const profile = this.getProfile(user);
      if (profile.email) {
        setLocalStorageData(undefined, LocalStorageKeys.UserName, profile.email);
      }

      logger.debug('Performing login API');
      const accessTokenApi = await this.getAccessToken([`${apiClientId}/.default`]);
      const login = await apiLoginUser(accessTokenApi);
      if (!login) throw new AppError('Back-end returned empty login');

      //the session was made by this provider, whatever the login answered: an API that predates the provider
      //fields, or an early return that left them at their default, must not route the rest of the session (the
      //trial start, the profile reload, the sign-out) to Microsoft
      login.identityProviderType = this.providerType;
      if (!login.tenantId) {
        login.tenantIdentityProviderType = this.providerType;
      }

      let currentTenant: Tenant | undefined = undefined;
      let currentUser: User | undefined = undefined;

      if (login.tenantLicensed && login.userLicensed) {
        currentTenant = login.tenants.find((t) => t.id === login.tenantId);
        currentUser = login.globalDataCache.users.items.find((u) => u.id === login.userId);
        if (!currentTenant) throw new AppError('Back-end did not return the current tenant');
        if (!login.userId || !currentUser) throw new AppError('Back-end did not return the current user');
      } else {
        //a new organization or an unlicensed user: what the token says, so the welcome screen can name them
        const domain = this.directoryOf(user) ?? '';
        currentTenant = login.tenants.find((t) => t.id === login.tenantId) ?? new Tenant(login.tenantId ?? '', domain);
        if (!currentTenant.providerTenantId) {
          currentTenant.providerTenantId = domain;
          currentTenant.identityProviderType = this.providerType;
        }
        const displayName = profile.name ?? profile.preferred_username ?? profile.email ?? '';
        currentUser = new User(login.userId ?? '', profile.email ?? '', displayName);
        currentUser.identityProviderType = this.providerType;
        currentUser.providerUserId = profile.sub;
      }

      await completeLoginProfile(
        login,
        currentTenant,
        currentUser,
        accessTokenApi,
        () => this.getUserPhoto(profile),
        authStateUpdate,
        setGlobalDataCache,
      );
    } catch (err) {
      logger.debug('Error', err);

      //a refusal of the identity layer (a personal account, another directory) would repeat on every load
      //with the same token, so the session goes and the welcome screen shows the reason
      if (getIdentityProviderErrorCode(err as AppError)) {
        this.removeUser();
      }

      authStateUpdate(false, CurrentUser.getEmptyUser(), false, err);
    }
  };

  //
  // Tokens: the id_token for the API, nothing for Microsoft
  //
  getAccessToken = async (scopes: string[], tenant?: Tenant, allowInteraction: boolean = true): Promise<string> => {
    if (!OidcAuthProvider.isApiScope(scopes)) {
      throw new AppError('The signed-in user has no Microsoft identity', AuthErrorCodes.MicrosoftIdentityRequired);
    }

    const user = this.loadUser();
    if (!user) {
      throw new AppError('login_required', 'login_required');
    }

    try {
      const usable = await this.getUsableUser(user, allowInteraction);

      return usable.id_token ?? '';
    } catch (err) {
      if (allowInteraction && OidcAuthProvider.isSignInAgainError(err)) {
        //the API holds nothing to renew with (an issuer without offline_access, a revoked grant): one silent
        //round trip through the issuer's session brings a fresh id_token and lands back on this route. Once per
        //tab: the guard is lifted only by a landing whose token has time left, so a session that is stale on
        //arrival cannot loop; a second failure ends on the welcome screen with the sign-in-again text
        this.removeUser();
        if (!getSessionStorageData(this.silentAttemptedKey)) {
          setSessionStorageData(this.silentAttemptedKey, 'true');
          await this.signinRedirect('none', this.directoryOf(user));

          //like MSAL's redirect branch: the sign-in is on its way and the caller gets an empty token
          return '';
        }
      }

      throw err;
    }
  };

  hasScopes = async (scopes: string[]): Promise<boolean> => {
    return OidcAuthProvider.isApiScope(scopes) && this.loadUser() !== undefined;
  };

  //admin consent is a Microsoft Entra notion; a Google organization consents in the Workspace admin console,
  //an organization's issuer in its own administration
  adminConsentScope = (): void => {
    logger.debug('adminConsentScope is not available for this identity provider');
  };

  getActiveDirectoryId = (): string | undefined => {
    const user = this.currentUser ?? this.loadUser();

    return user ? this.directoryOf(user) : undefined;
  };

  //
  // Helpers
  //
  private static isApiScope(scopes: string[]): boolean {
    return scopes.some((scope) => scope === `${apiClientId}/.default`);
  }

  private static isSignInAgainError(err: unknown): boolean {
    return err instanceof AppError && err.code === AuthErrorCodes.OidcSignInAgain;
  }

  private getProfile(user: OidcUser): OidcProfile {
    return (user.profile ?? {}) as OidcProfile;
  }

  //the directory of a session in the terms the tenant row uses: the Workspace domain, or the issuer
  private directoryOf(user: OidcUser): string | undefined {
    const profile = this.getProfile(user);

    return this.isGoogle ? profile.hd : profile.iss?.replace(/\/+$/, '');
  }

  //what the id_token still has, in seconds: the API validates that token, whatever the access token says
  private static secondsLeft(user: OidcUser): number {
    const profile = (user.profile ?? {}) as OidcProfile;
    const fromToken = typeof profile.exp === 'number' ? profile.exp - Math.floor(Date.now() / 1000) : Number.POSITIVE_INFINITY;

    return Math.min(user.expires_in ?? 0, fromToken);
  }

  //renew when less than this is left: five minutes, or half the lifetime for an issuer with shorter tokens
  private static renewThreshold(user: OidcUser): number {
    const profile = (user.profile ?? {}) as OidcProfile;
    const lifetime = typeof profile.exp === 'number' && typeof profile.iat === 'number' ? profile.exp - profile.iat : 0;

    return lifetime > 0 ? Math.min(renewThresholdSeconds, Math.floor(lifetime / 2)) : renewThresholdSeconds;
  }

  //BrowserRouter reads the address only on popstate; without the event it keeps rendering the root. The event's
  //state is null, which react-router's history accepts. A full navigation would reload the page and, on /start,
  //hit the "no automatic sign-in" branch with the session already stored
  private static restoreLocation(url: string): void {
    const target = url.startsWith('/') ? url : '/';
    window.history.replaceState({}, document.title, target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  //the client of this provider; for the organization sign-in it is built for the issuer in play
  private getClient(): OidcClient {
    if (this.client) {
      return this.client;
    }

    const info = this.loadIssuerInfo();
    if (!info) {
      throw new AppError('No organization sign-in is in progress', AuthErrorCodes.OidcIssuerUnknown);
    }

    this.client = new OidcClient(getOidcClientSettings(info));

    return this.client;
  }

  //the issuer of the organization sign-in: the one asked for, or the one of the session in play
  private resolveIssuerInfo = async (directoryId: string | undefined): Promise<OidcIssuerInfo> => {
    const stored = this.loadIssuerInfo();
    const wanted = directoryId?.replace(/\/+$/, '').toLowerCase();

    if (stored && (!wanted || stored.issuer.toLowerCase() === wanted)) {
      return stored;
    }

    const info = await apiGetOidcLoginInfo(undefined, directoryId);
    if (!info) {
      throw new AppError('The organization sign-in is not available for this directory', AuthErrorCodes.OidcIssuerUnknown);
    }

    this.storeIssuerInfo(info);

    return info;
  };

  //a session whose id_token still has time left, else a renewed one
  private getUsableUser = async (user: OidcUser, allowInteraction: boolean): Promise<OidcUser> => {
    if (OidcAuthProvider.secondsLeft(user) > OidcAuthProvider.renewThreshold(user)) {
      return user;
    }

    if (!this.renewInFlight) {
      this.renewInFlight = this.renew(user).finally(() => {
        this.renewInFlight = undefined;
      });
    }

    return await this.renewInFlight;
  };

  private renew = async (user: OidcUser): Promise<OidcUser> => {
    logger.debug(`Renewing the session through the API (${this.apiPath})`);

    try {
      const response = await http.post<RenewResponse>(`/identity/${this.apiPath}/renew`, { id_token: user.id_token });
      const renewed = response.data;

      if (!renewed?.id_token) {
        throw new AppError('The API returned no id_token', AuthErrorCodes.OidcSignInAgain);
      }

      const claims = OidcAuthProvider.decodeJwtPayload(renewed.id_token);
      const renewedUser = new OidcUser({
        id_token: renewed.id_token,
        access_token: renewed.access_token ?? '',
        token_type: renewed.token_type ?? 'Bearer',
        scope: renewed.scope,
        profile: { ...(user.profile ?? {}), ...claims } as OidcUser['profile'],
        expires_at: Math.floor(Date.now() / 1000) + (renewed.expires_in ?? 3600),
        session_state: null,
      });

      this.storeUser(renewedUser);

      return renewedUser;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        //revoked, expired, or the proof was too old: only a new sign-in helps
        throw new AppError('The session cannot be renewed; sign in again', AuthErrorCodes.OidcSignInAgain);
      }

      throw err instanceof AppError ? err : AppError.fromApiError(err);
    }
  };

  private signinRedirect = async (
    prompt: string | undefined,
    directoryId: string | undefined,
    options?: SignInRedirectOptions,
  ): Promise<void> => {
    const extraQueryParams: Record<string, string> = {};

    if (this.isGoogle) {
      extraQueryParams.access_type = 'offline';
      if (directoryId) {
        extraQueryParams.hd = directoryId;
      }
    } else {
      //the issuer decides the client: looked up by the directory asked for, or the one of the last sign-in
      const info = await this.resolveIssuerInfo(directoryId);
      this.client = new OidcClient(getOidcClientSettings(info));
    }

    //where to land after the round trip: the place the FIRST redirect of this sign-in started from, so that the
    //consent retry and a sign-in after a failed silent attempt keep it
    if (options?.returnUrl) {
      setSessionStorageData(SessionStorageKeys.OidcReturnUrl, options.returnUrl);
    } else if (!OidcAuthProvider.pendingReturnUrl()) {
      setSessionStorageData(SessionStorageKeys.OidcReturnUrl, window.location.pathname + window.location.search);
    }

    const client = this.getClient();

    //the library keeps the state of every sign-in it started in local storage; the ones that never landed
    //(a closed tab, Back at the issuer) would stay there for good
    try {
      await client.clearStaleState();
    } catch (err) {
      logger.debug('clearStaleState failed', err);
    }

    const request = await client.createSigninRequest({
      request_type: 'si:r',
      prompt: prompt,
      login_hint: options?.withLoginHint === false ? undefined : getLocalStorageData(undefined, LocalStorageKeys.UserName),
      extraQueryParams: extraQueryParams,
    });

    setSessionStorageData(SessionStorageKeys.OidcPendingProvider, this.providerType.toString());
    logger.debug(`Redirecting to the identity provider (${this.apiPath}, prompt=${prompt ?? 'default'})`);
    window.location.assign(request.url);
  };

  private mapRedirectError = (err: unknown): AppError | undefined => {
    if (err instanceof ErrorResponse) {
      switch (err.error) {
        //prompt=none without a session at the issuer, or the person closed the chooser: not an error, just no sign-in
        case 'login_required':
        case 'interaction_required':
        case 'consent_required':
        case 'account_selection_required':
          return undefined;
        //the administrator has not allowed the app (or the person declined on its behalf)
        case 'access_denied':
        case 'admin_policy_enforced':
        case 'org_internal':
          return new AppError(err.error_description ?? err.error ?? 'access_denied', AuthErrorCodes.OidcAdminPolicy);
        default:
          //the token proxy refused the account itself: a personal Google account outside any Workspace
          if (err.error_description?.startsWith('IDP0002')) {
            return new AppError(err.error_description, 'IDP0002');
          }

          return new AppError(err.error_description ?? err.error ?? 'Sign-in failed', AuthErrorCodes.OidcSignInAgain);
      }
    }

    if (err instanceof AppError) {
      return err;
    }

    return new AppError((err as Error)?.message ?? 'Sign-in failed', AuthErrorCodes.OidcSignInAgain);
  };

  private getUserPhoto = async (profile: OidcProfile): Promise<Blob | undefined> => {
    if (!profile.picture) {
      return undefined;
    }

    try {
      const response = await fetch(profile.picture);

      return response.ok ? await response.blob() : undefined;
    } catch (err) {
      logger.debug('Profile picture not available', err);

      return undefined;
    }
  };

  private loadUser = (): OidcUser | undefined => {
    const stored = getSessionStorageData(this.userKey);
    if (!stored) {
      this.currentUser = undefined;

      return undefined;
    }

    try {
      const user = OidcUser.fromStorageString(stored);
      this.currentUser = user;

      return user;
    } catch {
      this.currentUser = undefined;

      return undefined;
    }
  };

  private storeUser = (user: OidcUser): void => {
    this.currentUser = user;
    setSessionStorageData(this.userKey, user.toStorageString());
  };

  private removeUser = (): void => {
    this.currentUser = undefined;
    removeSessionStorageData(this.userKey);
  };

  //the two providers keep their sessions apart, so a Google session and an organization session coexist
  private get userKey(): SessionStorageKeys {
    return this.isGoogle ? SessionStorageKeys.OidcUser : SessionStorageKeys.OidcOrgUser;
  }

  //and their guards too: one provider's silent attempt must not use up the other's for the tab
  private get silentAttemptedKey(): SessionStorageKeys {
    return this.isGoogle ? SessionStorageKeys.OidcSilentAttempted : SessionStorageKeys.OidcOrgSilentAttempted;
  }

  private get pendingOrgUnitKey(): SessionStorageKeys {
    return this.isGoogle ? SessionStorageKeys.OidcPendingOrgUnit : SessionStorageKeys.OidcOrgPendingOrgUnit;
  }

  private loadIssuerInfo = (): OidcIssuerInfo | undefined => {
    const stored =
      getSessionStorageData(SessionStorageKeys.OidcIssuerInfo) ?? getLocalStorageData(undefined, LocalStorageKeys.LastOidcIssuer);
    if (!stored) {
      return undefined;
    }

    try {
      const info = JSON.parse(stored) as OidcIssuerInfo;

      //an issuer stored before the offline_access flag existed is looked up once more
      return info.issuer && info.clientId && info.authorizationEndpoint && typeof info.offlineAccessSupported === 'boolean'
        ? info
        : undefined;
    } catch {
      return undefined;
    }
  };

  private storeIssuerInfo = (info: OidcIssuerInfo): void => {
    const json = JSON.stringify(info);
    setSessionStorageData(SessionStorageKeys.OidcIssuerInfo, json);
    setLocalStorageData(undefined, LocalStorageKeys.LastOidcIssuer, json);
    this.client = undefined;
  };

  static decodeJwtPayload(token: string): Record<string, unknown> {
    try {
      const payload = token.split('.')[1] ?? '';
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const json = decodeURIComponent(
        Array.from(atob(padded))
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      );

      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
}
